// AIDEV-NOTE: Carrossel reutilizavel para fotos de produtos.
// Usa embla-carousel-react. Graceful degradation: placeholder se sem fotos, imagem simples se 1 foto.

import { useState, useEffect, useCallback } from 'react';
import { Box, IconButton, Skeleton } from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ImageNotSupported as ImageNotSupportedIcon
} from '@mui/icons-material';
import useEmblaCarousel from 'embla-carousel-react';

export interface ProdutoCarouselProps {
  fotos: string[];
  height?: number | string;
  showDots?: boolean;
  showNav?: boolean;
  loading?: boolean;
}

export function ProdutoCarousel({
  fotos,
  height = 220,
  showDots = true,
  showNav = true,
  loading = false
}: ProdutoCarouselProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((index: number) => emblaApi?.scrollTo(index), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    const onInit = () => {
      setScrollSnaps(emblaApi.scrollSnapList());
      onSelect();
    };

    onInit();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onInit);

    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onInit);
    };
  }, [emblaApi]);

  // Loading state
  if (loading) {
    return <Skeleton variant="rectangular" sx={{ width: '100%', height, borderRadius: 1 }} />;
  }

  // No photos placeholder
  if (fotos.length === 0) {
    return (
      <Box
        sx={{
          width: '100%',
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
          borderRadius: 1
        }}
      >
        <ImageNotSupportedIcon sx={{ fontSize: 48, color: 'grey.400' }} />
      </Box>
    );
  }

  // Single photo — no carousel controls
  if (fotos.length === 1) {
    return (
      <Box sx={{ width: '100%', height, borderRadius: 1, overflow: 'hidden' }}>
        <img
          src={fotos[0]}
          alt="Foto do produto"
          loading="lazy"
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      </Box>
    );
  }

  // Multiple photos — full carousel
  const hasMultiple = fotos.length > 1;

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <Box ref={emblaRef} sx={{ overflow: 'hidden', borderRadius: 1 }}>
        <Box sx={{ display: 'flex' }}>
          {fotos.map((url, i) => (
            <Box
              key={i}
              sx={{
                flex: '0 0 100%',
                minWidth: 0,
                height
              }}
            >
              <img
                src={url}
                alt={`Foto ${i + 1} do produto`}
                loading="lazy"
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </Box>
          ))}
        </Box>
      </Box>

      {/* Navigation arrows */}
      {showNav && hasMultiple && (
        <>
          <IconButton
            onClick={scrollPrev}
            size="small"
            sx={{
              position: 'absolute',
              left: 4,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(255,255,255,0.8)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' }
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            onClick={scrollNext}
            size="small"
            sx={{
              position: 'absolute',
              right: 4,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(255,255,255,0.8)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' }
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </>
      )}

      {/* Dots pagination */}
      {showDots && hasMultiple && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 0.5,
            mt: 1
          }}
        >
          {scrollSnaps.map((_, i) => (
            <Box
              key={i}
              onClick={() => scrollTo(i)}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: i === selectedIndex ? 'primary.main' : 'grey.300',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
